import {
  ExportP2PInFileAction,
  ExportP2PInReversalAction,
  ExportP2POutFileAction,
  ExportP2POutReversalAction,
} from "actions";

import { NlisFileRow } from "constants/columnDefinitions/nlisFile";
import { NLISFileTypes } from "constants/nlis";

interface RollbackCtx {
  saleyardName: string;
  rawSaleyardName: string;
}

export interface P2PLegFileTypeConfig {
  from: (
    transfer: NlisFileRow,
    ctx: { saleyardName: string },
  ) => string | null | undefined;
  to: (
    transfer: NlisFileRow,
    ctx: { saleyardName: string },
  ) => string | null | undefined;
  rollback: {
    message: (ctx: RollbackCtx) => string;
    reversalAction: { create: (payload: object) => unknown };
  };
  deleteAction: (id: string) => unknown;
  refreshAction: () => unknown;
}

// In and Out are mirror images: one side is always the saleyard, the
// other reads off the transfer.
const makeFromTo = (
  direction: "in" | "out",
  counterpartyField: "vendorDisplay" | "destinationDisplay",
): Pick<P2PLegFileTypeConfig, "from" | "to"> => {
  const counterparty = (transfer: NlisFileRow) => transfer[counterpartyField];
  const saleyard = (
    _transfer: NlisFileRow,
    { saleyardName }: { saleyardName: string },
  ) => saleyardName;
  return direction === "in"
    ? { from: counterparty, to: saleyard }
    : { from: saleyard, to: counterparty };
};

export const P2PLegFileTypeConfigs: Partial<
  Record<NLISFileTypes, P2PLegFileTypeConfig>
> = {
  [NLISFileTypes.NLISExportP2PInFile]: {
    ...makeFromTo("in", "vendorDisplay"),
    rollback: {
      // Plural "vendors" like Take, since one file can cover multiple.
      message: () =>
        "Rolling back this transfer will notify the NLIS that these animals are back in the possession of their respective vendors.",
      reversalAction: ExportP2PInReversalAction,
    },
    deleteAction: ExportP2PInFileAction.delete,
    refreshAction: () => ExportP2PInFileAction.request(),
  },
  [NLISFileTypes.NLISExportP2POutFile]: {
    ...makeFromTo("out", "destinationDisplay"),
    rollback: {
      message: ({ rawSaleyardName }) =>
        `Rolling back this transfer will notify the NLIS that these animals are back in the possession of ${rawSaleyardName}.`,
      reversalAction: ExportP2POutReversalAction,
    },
    deleteAction: ExportP2POutFileAction.delete,
    refreshAction: () => ExportP2POutFileAction.request(),
  },
};
